import { NavLink, useNavigate } from 'react-router-dom';
import Icon from '../ui/Icon';
import logo from '../../Assets/Images/logo.svg';
import { NAV_ITEMS } from './navItems';
import { clearSession, getCurrentUser } from '../../auth/session';

/*
 * Responsive behaviour (DESIGN.md › Layout):
 *   xl  (≥1280px)  full 260px sidebar
 *   lg  (1024–1279) 64px icon rail
 *   <lg            off-canvas drawer, opened from the top bar
 * From lg up, the top bar's ☰ button can hide it entirely (`desktopHidden`).
 * `full` = text that only shows on the full sidebar and in the drawer.
 */
const full = 'lg:hidden xl:block';

export default function Sidebar({ mobileOpen, desktopHidden, onClose }) {
  const navigate = useNavigate();
  const user = getCurrentUser();

  const logout = () => {
    clearSession();
    navigate('/login', { replace: true });
  };

  return (
    <>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-on-background/35 lg:hidden" onClick={onClose} aria-hidden="true" />
      )}

      <aside
        className={`fixed left-0 top-0 z-50 h-screen w-[260px] lg:w-16 xl:w-[260px] bg-surface-container-lowest border-r border-outline-variant/30 shadow-[2px_0_12px_rgba(0,0,0,0.02)] flex flex-col select-none transition-transform duration-200 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } ${desktopHidden ? 'lg:-translate-x-full' : 'lg:translate-x-0'}`}
      >
        <div className="relative h-16 flex items-center justify-center border-b border-surface-container">
          {/* The logo artwork has built-in padding, so scale it up to fill the header */}
          <img alt="Pompi" src={logo} className="h-14 w-14 lg:h-12 lg:w-12 xl:h-14 xl:w-14 object-contain scale-150" />
          <span
            className={`absolute right-4 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary-fixed text-on-primary-fixed tracking-wide uppercase ${full}`}
          >
            Admin
          </span>
        </div>

        <nav className="flex-1 px-3 lg:px-2 xl:px-3 py-4 space-y-1 overflow-y-auto">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onClose}
              title={item.label}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 lg:px-0 xl:px-3 lg:justify-center xl:justify-start py-2.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? 'bg-primary-container text-on-primary font-medium shadow-sm'
                    : 'text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
                }`
              }
            >
              <Icon name={item.icon} size={20} />
              <span className={`leading-snug ${full}`}>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 lg:p-2 xl:p-3 border-t border-surface-container bg-surface-container-low/60">
          <div className="flex items-center gap-2.5 p-2 lg:p-1 xl:p-2 lg:flex-col xl:flex-row rounded-lg bg-surface-container-lowest border border-outline-variant/30">
            <div
              className="w-8 h-8 rounded-full bg-primary-container flex items-center justify-center shrink-0 text-on-primary font-semibold text-xs"
              title={user.name}
            >
              {user.initials}
            </div>
            <div className={`flex-1 min-w-0 ${full}`}>
              <p className="text-xs font-semibold text-on-surface truncate leading-tight">{user.name}</p>
              <p className="text-[11px] text-secondary font-medium truncate">{user.role}</p>
            </div>
            <button
              type="button"
              onClick={logout}
              title="Log out"
              className="text-on-surface-variant hover:text-error transition-colors p-1 rounded"
            >
              <Icon name="logout" size={18} />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
